import React, { FC, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { RichTextContent } from "../../Unknown/RichTextContent";
import Typography from "../../Unknown/Typography";
import IconButton from "../../Unknown/IconButton";
import Button from "../../Unknown/Button";
import Box from "../../Unknown/Box";
import { CloseIcon } from "../../Unknown/Icons";
import NumberSpinner from "../../Unknown/NumberSpinner";
import { ConfirmedAppointment } from "../AppointmentConfirmedScreen/parseConfirmedAppointment";
import useTranslations from "./useTranslations";
import { AddOnAppliesTo } from "../AppointmentBookingContext/useBookingAddOns";
import Select, { SelectChangeEvent } from "../../Unknown/Select";
import MenuItem from "../../Unknown/MenuItem";
import InputLabel from "../../Unknown/InputLabel";
import FormControl from "../../Unknown/FormControl";
import FormHelperText from "../../Unknown/FormHelperText";
import { SelectedAddOnService } from "./useSuggestedAddOns";
import Checkbox from "../../Unknown/Checkbox";
import { SuggestedPackage } from "./useSuggestedPackages";
import { BookingPackage } from "../AppointmentBookingContext/useBookingPackages";

type AddOnOfferingType = "package" | "offering";

interface AddOnOfferingOption {
  type: AddOnOfferingType;
  label: string;
  value: string;
  isDisabled: boolean;
  price: number | null;
}

type SuggestedAddOnItemProps = {
  addOnId: string;
  addOnName: string;
  addOnPrice: number;
  appliedPrice: number;
  marketingText: string;
  appointmentData: ConfirmedAppointment;
  addedPackages: SuggestedPackage[];
  isSelected: boolean;
  appliesTo: AddOnAppliesTo;
  allowMultiplePurchases: boolean;
  quantity: number;
  selectedPackages: SelectedAddOnService[];
  selectedOfferings: SelectedAddOnService[];
  centerPackages: BookingPackage[];
  selectedPackageIds: Record<string, string[]>;
  selectedOfferingIds: Record<string, string[]>;
  error: Record<string, string | null>;
  calculatedAddOnPrice: number;
  setSelectedPackageIds: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  setSelectedOfferingIds: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  setError: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  onSelect: ({
    selectedPackageIds,
    selectedOfferingIds,
  }: {
    selectedPackageIds: string[];
    selectedOfferingIds: string[];
  }) => void;
  onDeselect: () => void;
  onQuantityChange: (quantity: number) => void;
};
const SuggestedAddOnItem: FC<SuggestedAddOnItemProps> = ({
  addOnId,
  addOnName,
  addOnPrice,
  marketingText,
  appointmentData,
  appliesTo,
  isSelected,
  allowMultiplePurchases,
  quantity,
  selectedPackages,
  selectedOfferings,
  appliedPrice,
  addedPackages,
  centerPackages,
  selectedOfferingIds,
  setSelectedOfferingIds,
  selectedPackageIds,
  setSelectedPackageIds,
  calculatedAddOnPrice,
  error,
  setError,
  onSelect,
  onDeselect,
  onQuantityChange,
}) => {
  const {
    translations: {
      addLabel,
      addOnOfferingsLabel,
      selectOfferingsError,
      appliedToLabel,
      fromLabel,
    },
  } = useTranslations();

  const addOnSelectedPackagesIds = useMemo(() => {
    return selectedPackageIds[addOnId] || [];
  }, [addOnId, selectedPackageIds]);
  const addOnSelectedOfferingIds = useMemo(() => {
    return selectedOfferingIds[addOnId] || [];
  }, [addOnId, selectedOfferingIds]);
  const addOnError = useMemo(() => {
    return error[addOnId];
  }, [addOnId, error]);

  const appliedSingleOfferings = useMemo(() => {
    return appointmentData.offerings.filter((offering) =>
      selectedOfferings.some(
        (selectedOffering) =>
          selectedOffering.appointmentOfferingId === offering.id &&
          selectedOffering.isAdded,
      ),
    );
  }, [appointmentData.offerings, selectedOfferings]);

  const appliedPackages = useMemo(() => {
    return centerPackages.filter((pkg) => {
      return selectedPackages.some(
        (selectedPackage) =>
          selectedPackage.id === pkg.id && selectedPackage.isAdded,
      );
    });
  }, [centerPackages, selectedPackages]);

  const { formatNumber } = useIntl();

  const options = useMemo(() => {
    if (appliesTo !== "study") {
      return [] as AddOnOfferingOption[];
    }

    const appointmentPackageOptionsLocal: AddOnOfferingOption[] =
      appointmentData.packages.map((pkg) => {
        const isPurchased = pkg.addOnIds.includes(addOnId);

        return {
          type: "package" as const,
          label: pkg.name,
          value: pkg.centerPackageId,
          isDisabled: isPurchased,
          price: isPurchased ? null : addOnPrice * pkg.offerings.length,
        };
      });

    const addedPackageOptionsLocal: AddOnOfferingOption[] = addedPackages.map(
      (pkg) => {
        const isPurchased = pkg.forOfferings.some((offeringId) => {
          return appointmentData.offerings.some((appointmentOffering) => {
            return (
              appointmentOffering.globalOfferingId === offeringId &&
              appointmentOffering.addOnIds.includes(addOnId)
            );
          });
        });

        return {
          type: "package" as const,
          label: pkg.centerPackage.name,
          value: pkg.centerPackage.id,
          isDisabled: isPurchased,
          price: isPurchased
            ? null
            : addOnPrice * pkg.centerPackage.globalStudyIds.length,
        };
      },
    );

    const offeringOptionsLocal: AddOnOfferingOption[] =
      appointmentData.offerings
        .filter((appointmentOffering) => {
          const isUpgraded = addedPackages.some((pkg) =>
            pkg.forOfferings.includes(appointmentOffering.globalOfferingId),
          );

          return !isUpgraded;
        })
        .map((offering) => {
          const isPurchased = offering.addOnIds.includes(addOnId);

          const label = offering.side
            ? `${offering.name} (${offering.side})`
            : offering.name;

          return {
            type: "offering" as const,
            label,
            value: offering.id,
            isDisabled: isPurchased,
            price: isPurchased ? null : addOnPrice,
          };
        });

    return [
      ...appointmentPackageOptionsLocal,
      ...addedPackageOptionsLocal,
      ...offeringOptionsLocal,
    ];
  }, [
    appliesTo,
    appointmentData.packages,
    appointmentData.offerings,
    addedPackages,
    addOnId,
    addOnPrice,
  ]);

  const activeOptions = useMemo(() => {
    return options.filter((option) => !option.isDisabled);
  }, [options]);

  const priceLabel = useMemo(() => {
    let price = addOnPrice;

    const isSingleService = activeOptions.length === 1;

    if (appliesTo === "study") {
      if (isSelected) {
        price = appliedPrice;
      } else {
        const activeOptionPrices = activeOptions
          .map((option) => option.price)
          .filter((optionPrice) => !Number.isNaN(optionPrice));

        const selectedOptionsPrice = activeOptions
          .filter((option) => {
            const isSelectedOffering =
              option.type === "offering" &&
              addOnSelectedOfferingIds?.includes(option.value);
            const isSelectedPackage =
              option.type === "package" &&
              addOnSelectedPackagesIds?.includes(option.value);

            return isSelectedOffering || isSelectedPackage;
          })
          .reduce((acc, option) => {
            return acc + (option?.price || 0);
          }, 0);

        const minOptionPrice = Math.min(...(activeOptionPrices as number[]));

        price = Math.max(selectedOptionsPrice, minOptionPrice) * quantity;
      }
    }

    if (appliesTo === "appointment") {
      price = calculatedAddOnPrice;
    }

    const formattedPrice = formatNumber(price, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    const isFromLabelShown =
      isSingleService ||
      appliesTo === "appointment" ||
      isSelected ||
      addOnSelectedPackagesIds?.length ||
      addOnSelectedOfferingIds?.length;

    return isFromLabelShown ? formattedPrice : `${fromLabel} ${formattedPrice}`;
  }, [
    activeOptions,
    addOnPrice,
    addOnSelectedOfferingIds,
    addOnSelectedPackagesIds,
    appliedPrice,
    appliesTo,
    calculatedAddOnPrice,
    formatNumber,
    fromLabel,
    isSelected,
    quantity,
  ]);

  const idToType = useMemo(() => {
    const map = new Map<string, AddOnOfferingType>();
    options.forEach((opt) => map.set(opt.value, opt.type));
    return map;
  }, [options]);

  const idToLabel = useMemo(() => {
    const map = new Map<string, string>();
    options.forEach((opt) => map.set(opt.value, opt.label));
    return map;
  }, [options]);

  const selectedValues = useMemo(
    () => [...addOnSelectedPackagesIds, ...addOnSelectedOfferingIds],
    [addOnSelectedPackagesIds, addOnSelectedOfferingIds],
  );

  const handleOfferingChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value as unknown;
      const selectedIds: string[] = Array.isArray(value)
        ? (value as string[])
        : [];

      const nextPackageIds = selectedIds.filter(
        (id) => idToType.get(id) === "package",
      );
      const nextOfferingIds = selectedIds.filter(
        (id) => idToType.get(id) === "offering",
      );

      const isValid = nextOfferingIds.length > 0 || nextPackageIds.length > 0;

      if (addOnError && isValid) {
        setError((prev) => {
          return { ...prev, [addOnId]: null };
        });
      }

      setSelectedPackageIds((prev) => ({ ...prev, [addOnId]: nextPackageIds }));
      setSelectedOfferingIds((prev) => ({
        ...prev,
        [addOnId]: nextOfferingIds,
      }));
    },
    [
      addOnError,
      addOnId,
      idToType,
      setError,
      setSelectedOfferingIds,
      setSelectedPackageIds,
    ],
  );

  const appliedToOfferings = useMemo(() => {
    const offerings = [...appliedPackages, ...appliedSingleOfferings].map(
      (item) => item.name,
    );

    return offerings.join(", ");
  }, [appliedSingleOfferings, appliedPackages]);

  const onSelectAddOn = useCallback(() => {
    const isStudy = appliesTo === "study";

    if (!isStudy) {
      onSelect({ selectedPackageIds: [], selectedOfferingIds: [] });
      return;
    }

    const isSingleService = activeOptions.length === 1;

    const hasNoSelection =
      addOnSelectedOfferingIds.length === 0 &&
      addOnSelectedPackagesIds.length === 0 &&
      !isSingleService;

    if (hasNoSelection) {
      setError((prev) => {
        return {
          ...prev,
          [addOnId]: selectOfferingsError,
        };
      });
      return;
    }

    if (!isSingleService) {
      onSelect({
        selectedPackageIds: addOnSelectedPackagesIds,
        selectedOfferingIds: addOnSelectedOfferingIds,
      });
    }

    if (isSingleService) {
      const option = activeOptions[0];

      const offeringIds = option.type === "offering" ? [option.value] : [];
      const packageIds = option.type === "package" ? [option.value] : [];

      onSelect({
        selectedOfferingIds: offeringIds,
        selectedPackageIds: packageIds,
      });
    }

    setSelectedOfferingIds({});
    setSelectedPackageIds({});
    setError({});
  }, [
    activeOptions,
    addOnId,
    addOnSelectedOfferingIds,
    addOnSelectedPackagesIds,
    appliesTo,
    onSelect,
    selectOfferingsError,
    setError,
    setSelectedOfferingIds,
    setSelectedPackageIds,
  ]);

  if (appliesTo !== "appointment" && !activeOptions.length) {
    return null;
  }

  return (
    <Box mt={2} p={3.25} border={1} borderColor="divider" borderRadius={1}>
      <Box
        display="flex"
        gap={{ xs: 4, md: 8 }}
        justifyContent={{ xs: "center", md: "space-between" }}
        flexDirection={{ xs: "column", md: "row" }}
      >
        <Box flexGrow={1} maxWidth={{ xs: "100%", md: "50%" }}>
          <Typography mb={0.5}>
            {isSelected && quantity > 1
              ? `${addOnName} x${quantity}`
              : addOnName}
          </Typography>
          <RichTextContent content={marketingText} />
          {appliesTo === "study" && !isSelected && activeOptions.length > 1 && (
            <FormControl fullWidth sx={{ maxWidth: 360, width: "100%", mt: 3 }}>
              <InputLabel
                id={`${addOnId}-select-offering`}
                size="small"
                error={!!addOnError}
              >
                {addOnOfferingsLabel}
              </InputLabel>
              <Select<string[]>
                labelId={`${addOnId}-select-offering`}
                id="demo-simple-select"
                multiple
                size="small"
                label={addOnOfferingsLabel}
                value={selectedValues}
                error={!!addOnError}
                onChange={handleOfferingChange}
                renderValue={(selected) =>
                  (selected as string[])
                    .map((id) => idToLabel.get(id) ?? "")
                    .join(", ")
                }
              >
                {options.map((option) => {
                  const isChecked =
                    [
                      ...addOnSelectedOfferingIds,
                      ...addOnSelectedPackagesIds,
                    ].includes(option.value) || option.isDisabled;

                  const formattedPrice = option.price
                    ? formatNumber(option.price, {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : "";

                  return (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={option.isDisabled}
                      sx={{ height: "36px" }}
                    >
                      <Checkbox
                        checked={isChecked}
                        disabled={option.isDisabled}
                      />
                      <Box sx={{ ml: 3 }}>{option.label}</Box>
                      <Box component="span" sx={{ ml: "auto", pl: 10 }}>
                        {formattedPrice}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
              {addOnError && (
                <FormHelperText error>{addOnError}</FormHelperText>
              )}
            </FormControl>
          )}
        </Box>
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          gap={{ xs: 2, md: 8 }}
        >
          <Box>
            {appliesTo === "study" && isSelected && (
              <Box display="flex" flexDirection="column" gap={0.5} mb={2}>
                <Typography variant="caption">{appliedToLabel}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {appliedToOfferings}
                </Typography>
              </Box>
            )}
          </Box>
          <Box>
            <Typography py={2} whiteSpace={{ xs: "normal", md: "nowrap" }}>
              {priceLabel}
            </Typography>
          </Box>
          <Box
            width={{ xs: "100%", md: "fit-content" }}
            display="flex"
            alignItems="center"
            gap={2}
          >
            {allowMultiplePurchases && (
              <NumberSpinner
                value={quantity}
                onChange={onQuantityChange}
                min={1}
              />
            )}
            {isSelected ? (
              <IconButton
                sx={{
                  p: 1,
                  "& svg": {
                    width: 35,
                    height: 35,
                  },
                }}
                color="error"
                onClick={onDeselect}
              >
                <CloseIcon />
              </IconButton>
            ) : (
              <Button
                size="large"
                variant="contained"
                color="success"
                onClick={onSelectAddOn}
              >
                {addLabel}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SuggestedAddOnItem;
